import { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ThermometerIcon, DropletsIcon, ZapIcon, ChevronLeftIcon, ChevronRightIcon, LockIcon
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

import { useTelemetry } from '../../hooks/useTelemetry';
import { useTelemetryHistory } from '../../hooks/useHistoricalTelemetry';
import { tbToChartData, tbToCombinedChartData } from '../../utils/tbChartHelper';
import { thingsboardService } from '../../services/thingsboard';

import type { SensorDetailSidebarProps, TimeRange, Mode } from './SensorDetailSidebar';
import { MODES, TimeRangeTabs, ChartSection, PasswordToast, ToggleSwitch, SensorHeader } from './SensorDetailSidebar';
import { useTranslation } from 'react-i18next';

// ─── SEO Detail View ───
export function SEODetail({ sensor, onBack, onClose, onUpdateSensor, onShowToast }: SensorDetailSidebarProps) {
  const { t } = useTranslation();
  const deviceId = sensor.id;
  const telemetryKeys = useMemo(() => ['power_instant', 'temperature', 'humidity', 'plug_on', 'plug_mode', 'active'], []);
  const { data: realtimeData } = useTelemetry(deviceId, telemetryKeys);

  const [envTimeRange, setEnvTimeRange] = useState<TimeRange>('daily');
  const [powerTimeRange, setPowerTimeRange] = useState<TimeRange>('daily');

  const powerKey = powerTimeRange === 'realtime' ? 'power_instant' :
                   powerTimeRange === 'daily' ? 'hourly_power' :
                   powerTimeRange === 'weekly' ? 'daily_power' : 'daily_power';

  const { data: histPower } = useTelemetryHistory(deviceId, [powerKey], powerTimeRange);
  const { data: histTempHumid } = useTelemetryHistory(deviceId, ['temperature', 'humidity'], envTimeRange);

  const finalPowerData = useMemo(() => {
    return powerTimeRange === 'realtime'
      ? tbToChartData(realtimeData, 'power_instant', 'realtime', [])
      : tbToChartData(histPower, powerKey, powerTimeRange, [], 0.001);
  }, [powerTimeRange, realtimeData, histPower, powerKey]);

  const finalTempHumidData = useMemo(() => {
    return envTimeRange === 'realtime'
      ? tbToCombinedChartData(realtimeData, 'temperature', 'temp', 'humidity', 'humidity', 'realtime', [])
      : tbToCombinedChartData(histTempHumid, 'temperature', 'temp', 'humidity', 'humidity', envTimeRange, []);
  }, [envTimeRange, realtimeData, histTempHumid]);

      const isOnline = Boolean(sensor.active);
  const [mode, setMode] = useState<Mode>('occupancy');
  const [isOn, setIsOn] = useState(true);

  useEffect(() => {
    const switchState = realtimeData?.['plug_on'];
    const plugModeState = realtimeData?.['plug_mode'];
    if (switchState && switchState.length > 0) {
      const val = switchState[0].value;
      setIsOn(String(val) === 'true' || String(val) === 'on');
    }
    if (plugModeState && plugModeState.length > 0) {
      const val = Number(plugModeState[0].value);
      const matchedMode = MODES.find((m) => Math.floor(m.value) === Math.floor(val));
      if (matchedMode) {
        setMode(matchedMode.id);
      }
    }
  }, [realtimeData]);

  const handleToggleOutlet = async () => {
    if (!isOnline) {
      onShowToast?.(t('sensor.device_not_connected', '기기가 연결되어 있지 않습니다.'), 'error');
      return;
    }
    const newValue = !isOn;
    setIsOn(newValue);
    try {
      await thingsboardService.sendOneWayRpc(deviceId, 'set_plug', { value: newValue?"on":"off"});
    } catch (error) {
      console.error('Failed to send RPC command for SEO plug switch:', error);
      setIsOn(!newValue);
    }
    setTimeout(() => {
      try {
        thingsboardService.sendOneWayRpc(deviceId, 'set_plug', { value: newValue?"on":"off"});
      } catch (error) {
        console.error('Failed to send RPC command for SEO plug switch:', error);
        setIsOn(!newValue); 
      }
    }, 100);
  };

  const [password, setPassword] = useState('0000');
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pendingMode, setPendingMode] = useState<Mode | null>(null);

  const applyModeChange = async (targetModeId: Mode) => {
    setMode(targetModeId);
    try {
      const targetMode = MODES.find((m) => m.id === targetModeId);
      if (targetMode) {
        await thingsboardService.sendOneWayRpc(deviceId, 'set_seo_mode', { value: targetMode.value });
      }
    } catch (error) {
      console.error('Failed to send RPC command for SEO plug mode:', error);
    }
  };

  const handleToggleMode = (newMode: Mode) => {
    if (!isOnline) {
      onShowToast?.(t('sensor.device_not_connected', '기기가 연결되어 있지 않습니다.'), 'error');
      return;
    }
    if (newMode === mode) return;

    // Both entering and exiting 'always' mode require password verification
    if (newMode === 'always' || mode === 'always') {
      setPendingMode(newMode);
      setShowPasswordVerify(true);
      return;
    }

    applyModeChange(newMode);
  };

  const handlePasswordVerified = (success: boolean) => {
    setShowPasswordVerify(false);
    if (success && pendingMode !== null) {
      applyModeChange(pendingMode);
    }
    setPendingMode(null);
  };

  const handlePasswordChanged = (newPw: string) => {
    setPassword(newPw);
    setShowPasswordChange(false);
  };

  return (
    <>
      <div className="w-full flex flex-col gap-3 p-3  min-h-full overflow-y-auto pb-10"
        style={{ borderColor: 'var(--mobile-border)', background: 'var(--mobile-bg-alt)' }}>
        <SensorHeader
          sensor={sensor}
          subtitle={sensor.type === 'SER' ? t('sensor.smart_edge_relay', '스마트 엣지 릴레이') : t('sensor.smart_edge_outlet')}
          onBack={onBack}
          onClose={onClose}
          onUpdateSensor={onUpdateSensor}
        />

        {/* Mode Selection - 4 Buttons + Toggle (Only in normal mode) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel rounded-xl p-2 flex-shrink-0 flex items-center justify-between gap-2"
        >
          {/* 4 Mode Buttons Group */}
          <div className={`grid grid-cols-4 gap-1 bg-black/40 p-1 rounded-xl border border-white/10 flex-1 transition-opacity ${
            !isOnline ? 'opacity-50' : ''
          }`}>
            {MODES.map((m) => {
              const isSelected = mode === m.id;
              const isAlways = m.id === 'always';
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handleToggleMode(m.id)}
                  className={`py-1.5 px-1 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-0.5 ${
                    !isOnline
                      ? isSelected
                        ? isAlways
                          ? 'bg-amber-500/50 text-slate-900 cursor-not-allowed'
                          : 'bg-cyan-500/50 text-slate-900 cursor-not-allowed'
                        : 'text-slate-500 hover:text-slate-400 cursor-not-allowed'
                      : isSelected
                      ? isAlways
                        ? 'bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20 active:scale-95'
                        : 'bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20 active:scale-95'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-white/5 active:scale-95'
                  }`}
                >
                  {isAlways && <LockIcon className="w-3 h-3" />}
                  <span>{m.label}</span>
                </button>
              );
            })}
          </div>

          {/* Right side: Toggle (Normal mode only) + Password button (Always mode only) */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <AnimatePresence>
              {mode === 'always' && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={() => {
                    if (!isOnline) {
                      onShowToast?.(t('sensor.device_not_connected', '기기가 연결되어 있지 않습니다.'), 'error');
                      return;
                    }
                    setShowPasswordChange(true);
                  }}
                  className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/25 text-amber-400 hover:bg-amber-500/25 transition-all active:scale-95 flex-shrink-0"
                  title="비밀번호 설정"
                >
                  <LockIcon className="w-4 h-4" />
                </motion.button>
              )}
            </AnimatePresence>

            {mode === 'normal' && (
              <ToggleSwitch isOn={isOn} onToggle={handleToggleOutlet} disabled={!isOnline} />
            )}
          </div>
        </motion.div>

        <ChartSection
          icon={ZapIcon}
          iconColor="text-violet-400"
          title="전기사용량"
          data={finalPowerData}
          timeRange={powerTimeRange}
          onTimeRangeChange={setPowerTimeRange}
          disabledRanges={!sensor.active ? ['realtime'] : []}
          chartType="area"
          color="#a78bfa"
          gradientId="seoPower"
          className="h-[280px]"
          unit={powerTimeRange === 'realtime' ? 'W' : 'kWh'}
          stats={[
            {
              label: powerTimeRange === 'realtime' ? '현재 전력사용량' :
                     powerTimeRange === 'daily' ? '오늘 사용량' :
                     powerTimeRange === 'weekly' ? '이번주 사용량' : '이번달 사용량',
              value: powerTimeRange === 'realtime'
                ? `${Number(realtimeData?.['power_instant']?.[0]?.value || 0).toFixed(1)} W`
                : `${finalPowerData.length > 0 ? finalPowerData.reduce((sum, item) => sum + (Number(item.value) || 0), 0).toFixed(3) : '0.000'} kWh`,
              color: 'text-violet-400'
            }
          ]}
        />

        {/* Combined Temperature + Humidity Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel rounded-xl p-3 h-[280px] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <ThermometerIcon className="w-3.5 h-3.5 text-rose-400" />
              <DropletsIcon className="w-3 h-3 text-cyan-400 -ml-1" />
              <span className="text-[0.9rem] font-bold text-slate-300">
                {t('sensor.indoor_temp_humidity')}
              </span>
            </div>
            <TimeRangeTabs value={envTimeRange} onChange={setEnvTimeRange} disabledRanges={!sensor.active ? ['realtime'] : []} />
          </div>

          {/* Current Values */}
          <div className="flex items-center gap-3 mb-2 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-rose-400" />
              <span className="text-[0.8rem] uppercase font-bold" style={{ color: 'var(--mobile-text-muted)' }}>온도</span>
              <span className="text-base font-mono font-bold text-rose-400">
                {finalTempHumidData.length > 0 && finalTempHumidData[finalTempHumidData.length - 1].temp ? finalTempHumidData[finalTempHumidData.length - 1].temp.toFixed(1) : '--'}°C
              </span>
            </div>
            <div className="h-3.5 w-px bg-white/10" />
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
              <span className="text-[0.8rem] uppercase font-bold" style={{ color: 'var(--mobile-text-muted)' }}>습도</span>
              <span className="text-base font-mono font-bold text-cyan-400">
                {finalTempHumidData.length > 0 && finalTempHumidData[finalTempHumidData.length - 1].humidity ? finalTempHumidData[finalTempHumidData.length - 1].humidity.toFixed(1) : '--'}%
              </span>
            </div>
          </div>

          {/* Combined Chart */}
          <div className="flex-1 min-h-0 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={finalTempHumidData}>
                <defs>
                  <linearGradient id="seoTempGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fb7185" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#fb7185" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="seoHumidGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" hide />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--mobile-bg-card)',
                    borderColor: 'var(--mobile-border-strong)',
                    fontSize: '0.7rem',
                    borderRadius: '8px'
                  }}
                  formatter={(value: number, name: string) => {
                    if (name === 'temp') return [`${Number(value).toFixed(1)}°C`, '온도'];
                    return [`${Number(value).toFixed(1)}%`, '습도'];
                  }}
                />
                <Area type="monotone" dataKey="temp" stroke="#fb7185" strokeWidth={2} fillOpacity={1} fill="url(#seoTempGrad)" />
                <Area type="monotone" dataKey="humidity" stroke="#22d3ee" strokeWidth={1.5} fillOpacity={1} fill="url(#seoHumidGrad)" strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Averages */}
          <div className="flex justify-between mt-1.5 text-xs flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 rounded-full bg-rose-400" />
              <span className="text-slate-500 font-medium text-[0.8rem]">평균</span>
              <span className="font-medium text-[0.8rem]" style={{ color: 'var(--detail-avg-value)' }}>
                {finalTempHumidData.length > 0
                  ? `${(finalTempHumidData.reduce((sum: number, item: any) => sum + (item.temp || 0), 0) / finalTempHumidData.length).toFixed(1)}°C`
                  : '--'}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 rounded-full bg-cyan-400 opacity-60" style={{ borderTop: '1px dashed #22d3ee' }} />
              <span className="text-slate-500 font-medium text-[0.8rem]">평균</span>
              <span className="font-medium text-[0.8rem]" style={{ color: 'var(--detail-avg-value)' }}>
                {finalTempHumidData.length > 0
                  ? `${(finalTempHumidData.reduce((sum: number, item: any) => sum + (item.humidity || 0), 0) / finalTempHumidData.length).toFixed(1)}%`
                  : '--'}
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Password Verify Toast */}
      <AnimatePresence>
        {showPasswordVerify && (
          <PasswordToast
            type="verify"
            storedPassword={password}
            onClose={() => {
              setShowPasswordVerify(false);
              setPendingModeIndex(null);
            }}
            onVerify={handlePasswordVerified}
          />
        )}
      </AnimatePresence>

      {/* Password Change Toast */}
      <AnimatePresence>
        {showPasswordChange && (
          <PasswordToast
            type="change"
            storedPassword={password}
            onClose={() => setShowPasswordChange(false)}
            onChangeComplete={handlePasswordChanged}
          />
        )}
      </AnimatePresence>
    </>
  );
}



